import { createContext, useContext, useMemo, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'

interface LocaleContextType {
  locale: string
  setLocale: (locale: string) => void
  isSupported: boolean
  supportedLocales: string[]
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { locale } = useParams<{ locale: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const supportedLocales = ['en', 'pt', 'es']

  const currentLocale = locale && supportedLocales.includes(locale) ? locale : 'en'
  const isSupported = supportedLocales.includes(currentLocale)

  const setLocale = useCallback(
    (newLocale: string) => {
      if (!supportedLocales.includes(newLocale)) {
        console.warn(`Unsupported locale: ${newLocale}`)
        return
      }

      // Remove old locale prefix and add new one
      const pathWithoutLocale = location.pathname.replace(/^\/(en|pt|es)/, '')
      const newPath = `/${newLocale}${pathWithoutLocale || '/'}`

      // Preserve query params and hash
      const fullPath = newPath + location.search + location.hash
      navigate(fullPath)
    },
    [location, navigate, supportedLocales]
  )

  const value = useMemo(
    () => ({
      locale: currentLocale,
      setLocale,
      isSupported,
      supportedLocales,
    }),
    [currentLocale, setLocale, isSupported, supportedLocales]
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) throw new Error('useLocale must be used within LocaleProvider')
  return context.locale
}

export function useSetLocale() {
  const context = useContext(LocaleContext)
  if (!context) throw new Error('useSetLocale must be used within LocaleProvider')
  return context.setLocale
}

export function useLocaleContext() {
  const context = useContext(LocaleContext)
  if (!context) throw new Error('useLocaleContext must be used within LocaleProvider')
  return context
}
